import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteLayout from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { SPECIAL_SERVICES, SITE, CITY_PAGES, CITY_DATA, SERVICES } from "@/data/siteContent";
import { usePageMeta } from "@/hooks/usePageMeta";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
  viewport: { once: true },
};

const SpecialServicePage = ({ language = "fr", serviceKey }) => {
  const service = SPECIAL_SERVICES[serviceKey] || SPECIAL_SERVICES.cleaning;
  const isFr = language === "fr";
  const content = service[language] || service.fr;
  const hasCustomHtml = Boolean(content.customHtml);

  usePageMeta(content.metaTitle, content.metaDescription);

  return (
    <SiteLayout language={language}>
      {hasCustomHtml ? (
        <section className="bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24" data-testid={`special-${service.key}-custom-html`}>
            <div dangerouslySetInnerHTML={{ __html: content.customHtml }} />
          </div>
        </section>
      ) : (
        <>
          <section className="bg-gradient-to-br from-slate-50 to-white">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 space-y-6">
              <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="special-service-eyebrow">
                {isFr ? "Service spécialisé" : "Specialized service"}
              </p>
              <h1 className="text-3xl md:text-5xl font-bold text-primary" data-testid={`special-${service.key}-h1`}>
                {content.h1}
              </h1>
              <p className="text-lg text-muted-foreground" data-testid={`special-${service.key}-intro`}>
                {content.intro}
              </p>
            </div>
          </section>

          <section className="py-16 md:py-24">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 space-y-10">
              {content.sections.map((section, index) => (
                <motion.div key={section.title} {...fadeUp} className="space-y-3">
                  <h2
                    className="text-2xl font-bold text-primary"
                    data-testid={`special-${service.key}-section-${index + 1}-title`}
                  >
                    {section.title}
                  </h2>
                  <p
                    className="text-muted-foreground"
                    data-testid={`special-${service.key}-section-${index + 1}-text`}
                  >
                    {section.text}
                  </p>
                </motion.div>
              ))}

              <motion.p
                {...fadeUp}
                className="text-sm text-muted-foreground"
                data-testid={`special-${service.key}-seo-line`}
              >
                {content.seoLine}
              </motion.p>
            </div>ZZ
          </section>

          <section className="py-16 md:py-24 bg-slate-900 text-white">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
              <motion.div {...fadeUp} className="space-y-4">
                <h2 className="text-3xl font-bold" data-testid={`special-${service.key}-cta-title`}>
                  {isFr ? "Obtenir une soumission gratuite" : "Get a free estimate"}
                </h2>
                <p className="text-slate-200" data-testid={`special-${service.key}-cta-text`}>
                  {content.ctaText}
                </p>
                <div className="flex flex-wrap gap-4 items-center">
                  <Button
                    asChild
                    className="rounded-none uppercase tracking-wider font-bold"
                    data-testid={`special-${service.key}-cta-button`}
                  >
                    <Link to={isFr ? "/fr/contact" : "/en/contact"}>
                      {isFr ? "Obtenir une soumission gratuite" : "Get a free estimate"}
                    </Link>
                  </Button>
                  <a
                    href={`tel:${SITE.phone}`}
                    className="text-lg font-semibold"
                    data-testid={`special-${service.key}-cta-phone`}
                  >
                    {SITE.phoneDisplay}
                  </a>
                </div>
              </motion.div>
            </div>
          </section>
        </>
            )}
           <section className="py-16 md:py-24">
  <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
    <h2 className="text-2xl font-bold text-primary mb-6">
      {isFr
        ? `${serviceKey === "cleaning" ? "Nettoyage" : "Scellant protecteur"} de pavé uni par ville`
        : `Paver ${serviceKey === "cleaning" ? "cleaning" : "sealing"} by city`}
    </h2>
    <ul className="space-y-2">
      {CITY_DATA.map((city) => {
        const svc = SERVICES[serviceKey];
        const path = isFr
          ? `/fr/villes/${city.slug}/${svc.slugFr}`
          : `/en/cities/${city.slug}/${svc.slugEn}`;
        return (
          <li key={city.slug}>
            <Link to={path}>
              {isFr
                ? `${serviceKey === "cleaning" ? "Nettoyage" : "Scellant"} de pavé uni à ${city.nameFr}`
                : `Paver ${serviceKey === "cleaning" ? "cleaning" : "sealing"} in ${city.nameEn}`}
            </Link>
          </li>
        );
      })}
    </ul>
  </div>
</section>
    </SiteLayout>
  );
};

export default SpecialServicePage;